import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { moneyFormatFromCents } from '../../../utilities/moneyFormat';
import { openImportCategoryRows } from '../../../redux/actions/import.js';
import { settingsTabChange } from '../../../redux/actions/settings.js';
import Button from '../../components/Button.jsx';
import CategorySelect from '../../components/CategorySelect.jsx';
import CircleLoader from '../../components/CircleLoader.jsx';
import ImportCategoryRowsModal from './ImportCategoryRowsModal.jsx';
import { IMPORT_RULES_TAB } from './ImportRules.jsx';
import {
	applyChoice,
	EXTRA_OPTION_GROUPS,
	isDecided,
	RULE_NONE,
	valueOf,
} from './importCategoryRule.js';

/** How many merchants an opened category shows before asking. A broad bucket can hold hundreds. */
const MERCHANTS_SHOWN = 25;

/**
 * One rule per category the export used, and narrower ones per merchant inside it.
 *
 * This is the half of the mapping that decides what a row *means*, where the account mapper decides
 * where it came from, and a transaction stays blocked until both have an answer. Rules are stored
 * against the budget rather than the file, so the next export arrives already answered.
 */
export default function ImportCategoryMapper() {
	const dispatch = useDispatch();
	const batch = useSelector((state) => state.importing.selectedBatch);
	const categories = useSelector((state) => state.importing.categories);
	const availableCategories = useSelector((state) => state.importing.availableCategories);
	const loading = useSelector((state) => state.importing.loadingCategories);
	const [hideDecided, setHideDecided] = React.useState(false);

	if(!batch || !categories.length) {
		return null;
	}

	const decidedCount = categories.filter(isDecided).length;
	const shown = hideDecided ? categories.filter((category) => !isDecided(category)) : categories;

	return (
		<div className='import-category-mapper'>
			<h6>
				Categories{' '}
				<span className='grey-text'>({decidedCount} of {categories.length} decided)</span>
				{loading && <CircleLoader size='small' />}
			</h6>
			<p className='grey-text'>
				Each category the export used needs to say what it means here: a category in this budget,
				a budget transaction left uncategorized to file later in the budget, a bank record with no
				budget transaction (transfers and card payments, which are real movement but not
				spending), or nothing at all. A broad export bucket can be split up by
				merchant, and a merchant&rsquo;s own rule wins over the one covering its category. The
				count and total on each line open the transactions behind it.
			</p>
			<p className='grey-text import-rules-link'>
				Rules naming an account, an amount or part of the statement, and the order rules are tried
				in, are on the{' '}
				<button
					type='button'
					className='btn-flat import-rows-link'
					onClick={() => dispatch(settingsTabChange(IMPORT_RULES_TAB))}
				>
					Import rules
				</button>
				{' '}page.
			</p>

			<label className='import-category-filter'>
				<input
					type='checkbox'
					className='filled-in'
					checked={hideDecided}
					onChange={(event) => setHideDecided(event.target.checked)}
				/>
				<span>Hide the ones already decided</span>
			</label>

			{!shown.length &&
			<p className='grey-text'>Every category in this file has a rule.</p>
			}

			{shown.map((category) => (
				<CategoryRule
					key={category.external_category}
					category={category}
					availableCategories={availableCategories}
					onChoose={(merchant, value) =>
						dispatch(applyChoice(batch.id, category.external_category, merchant, value))}
					onShowRows={(merchant) =>
						dispatch(openImportCategoryRows(batch.id, category.external_category, merchant))}
				/>
			))}

			<ImportCategoryRowsModal />
		</div>
	);
}

/**
 * How many rows a rule covers and what they come to, as a way in to the rows themselves.
 *
 * The total is the file's own sign -- negative for money out -- because that is what the export
 * says and the rules have not been applied to it yet. A count on its own does not say what a
 * decision is worth: forty rows can be lunches or mortgage payments.
 */
function RowsLink({ rowCount, amountTotal, onClick }) {
	return (
		<button type='button' className='btn-flat import-rows-link' onClick={onClick}>
			{rowCount} {rowCount === 1 ? 'row' : 'rows'} &middot; {moneyFormatFromCents(amountTotal)}
		</button>
	);
}

/**
 * One export category: the rule covering all of it, and the merchants inside it that can be given
 * their own.
 */
function CategoryRule({ category, availableCategories, onChoose, onShowRows }) {
	const [expanded, setExpanded] = React.useState(false);
	const [showAllMerchants, setShowAllMerchants] = React.useState(false);

	const merchants = category.merchants || [];
	const overrides = merchants.filter((merchant) => !!merchant.map).length;
	const visible = showAllMerchants ? merchants : merchants.slice(0, MERCHANTS_SHOWN);

	return (
		<div className={'import-category' + (isDecided(category) ? '' : ' import-category-undecided')}>
			<div className='import-category-head'>
				<div className='import-category-name'>
					{/**
					  * A file can leave the category column empty, and unlike an empty merchant that is
					  * still a rule of its own -- '' is only spoken for on the merchant side. So it is
					  * mapped like any other, and only needs a name to be shown under.
					  */}
					<span className='import-external-key'>
						{category.external_category || '(no category)'}
					</span>
					<span className='grey-text'>
						<RowsLink
							rowCount={category.row_count}
							amountTotal={category.amount_total}
							onClick={() => onShowRows(null)}
						/>
						{' '}&middot; {merchants.length} {merchants.length === 1 ? 'merchant' : 'merchants'}
						{overrides > 0 && <> &middot; {overrides} with their own rule</>}
					</span>
				</div>
				<CategoryRuleSelect
					availableCategories={availableCategories}
					map={category.map}
					onChoose={(value) => onChoose('', value)}
				/>
				<Button small flat onClick={() => setExpanded(!expanded)}>
					{expanded ? 'Hide merchants' : 'Split by merchant'}
				</Button>
			</div>

			{expanded &&
			<div className='import-category-merchants'>
				<table className='import-table'>
					<thead>
						<tr>
							<th>Merchant</th>
							<th className='right-align'>Rows</th>
							<th className='right-align'>Total</th>
							<th>Rule</th>
						</tr>
					</thead>
					<tbody>
						{visible.map((merchant, index) => (
							<MerchantRule
								key={merchant.merchant === null ? `__none__${index}` : merchant.merchant}
								merchant={merchant}
								availableCategories={availableCategories}
								onChoose={(value) => onChoose(merchant.merchant, value)}
								onShowRows={() => {
									/**
									 * '' rather than the null the file left, because that is the group the rows with
									 * no merchant form -- asking with null would open the whole category instead.
									 */
									onShowRows(merchant.merchant || '');
								}}
							/>
						))}
					</tbody>
				</table>
				{merchants.length > visible.length &&
				<Button small flat onClick={() => setShowAllMerchants(true)}>
					Show all {merchants.length} merchants
				</Button>
				}
			</div>
			}
		</div>
	);
}

function MerchantRule({ merchant, availableCategories, onChoose, onShowRows }) {
	/**
	 * A row can arrive with no merchant at all, and the empty string is already the key the rule
	 * covering the whole category is stored under -- so these cannot be given one of their own
	 * without overwriting it. They are listed anyway, because they are rows in the file and their
	 * count belongs on screen; the category's rule is what decides them.
	 */
	if(!merchant.mappable) {
		return (
			<tr className='import-category-merchant-unmappable'>
				<td className='grey-text'>(no merchant)</td>
				<td className='right-align'>
					<button type='button' className='btn-flat import-rows-link' onClick={onShowRows}>
						{merchant.row_count}
					</button>
				</td>
				<td className='right-align grey-text'>{moneyFormatFromCents(merchant.amount_total)}</td>
				<td className='grey-text'>Follows the category</td>
			</tr>
		);
	}

	return (
		<tr>
			<td>{merchant.merchant}</td>
			<td className='right-align'>
				<button type='button' className='btn-flat import-rows-link' onClick={onShowRows}>
					{merchant.row_count}
				</button>
			</td>
			<td className='right-align grey-text'>{moneyFormatFromCents(merchant.amount_total)}</td>
			<td>
				<CategoryRuleSelect
					availableCategories={availableCategories}
					map={merchant.map}
					blankLabel='Same as the category'
					merchantName={merchant.merchant}
					onChoose={onChoose}
				/>
			</td>
		</tr>
	);
}

/**
 * The choice itself, made with the same picker the transaction screens categorise with -- so a
 * category reads here exactly as it does everywhere else, sub-category names and colours included,
 * and there is one picker in this app to learn rather than two.
 *
 * On a merchant row it is handed the merchant's name, which CategorySelect turns into suggestions
 * drawn from how that merchant has been categorised before. That is the whole reason the import
 * screen wants this control rather than one of its own.
 */
function CategoryRuleSelect({ availableCategories, map, blankLabel, merchantName, onChoose }) {
	const value = valueOf(map);
	const assigned = typeof value === 'number' && value !== RULE_NONE
		? (availableCategories || []).find((category) => category.id === value)
		: null;

	return (
		<div className='import-category-choice'>
			<CategorySelect
				value={value}
				blank={blankLabel || 'No rule yet'}
				extraOptionGroups={EXTRA_OPTION_GROUPS}
				transactionName={merchantName}
				onChange={(option) => onChoose(option ? option.value : RULE_NONE)}
			/>
			{/**
			  * Which group the chosen category sits in, and what kind it is. CategorySelect heads its
			  * menu with the group's name but never its type, and the type is the half that bites:
			  * Import_Amount_Converter reads an income category's amount with the file's sign and
			  * everything else against it, so a paycheck filed under an expense group imports every
			  * payslip backwards with the totals still adding up.
			  */}
			{!!assigned &&
			<div className='import-suggestion grey-text'>
				{assigned.group_name} &middot; {assigned.group_type_name}
			</div>
			}
			{/**
			  * The rule is still stored, and blocks its rows rather than falling back to anything --
			  * so say why a line that was decided shows blank, and that the rows are waiting on it.
			  */}
			{!!map && !!map.category_deleted &&
			<div className='import-suggestion'>
				Its category was deleted, so these rows wait until you choose another or restore it.
			</div>
			}
		</div>
	);
}
